import http from '@/services/http';
import type { VariboxContractValue, VariboxRecord, VariboxStatusKey } from '@/data/variboxMockData';

export interface ApiVaribox {
  id: string;
  serial_number: string;
  radex_identifier: string;
  barcode: string;
  capacity_l: number;
  color: string;
  seal_type: string;
  status: string;
  contract: boolean;
  rfid_tag: string | null;
  current_location: {
    id: string;
    code: string | null;
    name: string;
  } | null;
  current_client: {
    id: string;
    name: string;
    active: boolean;
  } | null;
  notes: string | null;
  availability: 'enabled' | 'disabled';
  created_at: string;
  updated_at: string;
}

export interface ApiVariboxListResponse {
  items: ApiVaribox[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateVariboxPayload {
  serial_number: string;
  radex_identifier: string;
  barcode: string;
  capacity_l: number;
  color: string;
  seal_type: string;
  status: string;
  contract: boolean;
  current_location_id: string;
  rfid_tag?: string | null;
  current_client_id?: string | null;
  notes?: string | null;
}

export interface UpdateVariboxPayload {
  serial_number?: string;
  radex_identifier?: string;
  barcode?: string;
  capacity_l?: number;
  color?: string;
  seal_type?: string;
  status?: string;
  contract?: boolean;
  rfid_tag?: string | null;
  current_location_id?: string | null;
  current_client_id?: string | null;
  notes?: string | null;
}

export interface VariboxDuplicateFields {
  serial_number: boolean;
  radex_identifier: boolean;
  barcode: boolean;
}

export interface BulkDeleteVariboxesResponse {
  deleted_count: number;
  deleted_ids: string[];
  not_found_ids: string[];
}

export interface ListVariboxParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  locationId?: string;
  clientId?: string;
  includeLocationDescendants?: boolean;
}

export type VariboxViewRecord = Omit<VariboxRecord, 'capacityL' | 'client' | 'sealType'> & {
  capacityL: VariboxRecord['capacityL'];
  client: string;
  sealType: VariboxRecord['sealType'];
  id: string;
  barcode: string;
  availability: 'enabled' | 'disabled';
  currentLocationId: string | null;
  currentClientId: string | null;
  createdAtIso: string;
  updatedAtIso: string;
};

function toStatusKey(value: string): VariboxStatusKey {
  const normalized = value.trim().toLowerCase();

  if (normalized === 'wolny') {
    return 'available';
  }

  if (normalized === 'zajęty' || normalized === 'zajety') {
    return 'occupied';
  }

  if (normalized === 'uszkodzony') {
    return 'damaged';
  }

  if (normalized === 'available' || normalized === 'occupied' || normalized === 'damaged' || normalized === 'repair' || normalized === 'cleaning' || normalized === 'blocked') {
    return normalized as VariboxStatusKey;
  }

  return 'available';
}

function toContractValue(value: boolean): VariboxContractValue {
  return value ? 'tak' : 'nie';
}

function formatDateLabel(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return date.toLocaleString();
}

function formatLocationLabel(location: ApiVaribox['current_location']) {
  if (!location) {
    return '-';
  }

  const code = location.code?.trim() ?? '';
  const name = location.name.trim();

  if (!code) {
    return name;
  }

  if (code.toLowerCase() === name.toLowerCase()) {
    return name;
  }

  return `${code} ${name}`.trim();
}

export function mapApiVariboxToView(item: ApiVaribox): VariboxViewRecord {
  const normalizedSealType = item.seal_type.trim().toUpperCase();

  return {
    id: item.id,
    barcode: item.barcode,
    serialNumber: item.serial_number,
    radexId: item.radex_identifier,
    capacityL: item.capacity_l as VariboxRecord['capacityL'],
    bodyColor: item.color,
    sealType: normalizedSealType === 'EPDM' ? 'EPDM' : 'VITON',
    status: toStatusKey(item.status),
    client: item.current_client?.name ?? 'Nieprzypisany',
    contract: toContractValue(item.contract),
    rfid: item.rfid_tag ? 'assigned' : 'brak',
    lastLocation: formatLocationLabel(item.current_location),
    lastMovement: item.notes ?? '-',
    lastUpdatedAt: formatDateLabel(item.updated_at),
    history: [],
    availability: item.availability,
    currentLocationId: item.current_location?.id ?? null,
    currentClientId: item.current_client?.id ?? null,
    createdAtIso: item.created_at,
    updatedAtIso: item.updated_at,
  };
}

export async function listVariboxes(params: ListVariboxParams = {}) {
  const response = await http.get<ApiVariboxListResponse>('/api/variboxes', {
    params: {
      limit: params.limit ?? 200,
      offset: params.offset ?? 0,
      search: params.search || undefined,
      status: params.status || undefined,
      location_id: params.locationId || undefined,
      include_location_descendants: params.includeLocationDescendants || undefined,
      client_id: params.clientId || undefined,
    },
  });

  return response.data;
}

export async function getVariboxById(id: string) {
  const response = await http.get<ApiVaribox>(`/api/variboxes/${id}`);
  return response.data;
}

export async function createVaribox(payload: CreateVariboxPayload) {
  const response = await http.post<ApiVaribox>('/api/variboxes', payload);
  return response.data;
}

export async function updateVaribox(id: string, payload: UpdateVariboxPayload) {
  const response = await http.patch<ApiVaribox>(`/api/variboxes/${id}`, payload);
  return response.data;
}

export async function checkVariboxDuplicates(input: {
  serial_number?: string;
  radex_identifier?: string;
  barcode?: string;
  exclude_id?: string;
}) {
  const response = await http.get<VariboxDuplicateFields>('/api/variboxes/check-duplicates', {
    params: {
      serial_number: input.serial_number?.trim() || undefined,
      radex_identifier: input.radex_identifier?.trim() || undefined,
      barcode: input.barcode?.trim() || undefined,
      exclude_id: input.exclude_id,
    },
  });
  return response.data;
}

export async function assignScannedVariboxLocation(id: string, locationId: string) {
  const response = await http.post<ApiVaribox>(`/api/variboxes/${id}/scan-location`, {
    current_location_id: locationId,
  });
  return response.data;
}

export async function deleteVaribox(id: string) {
  await http.delete(`/api/variboxes/${id}`);
}

export async function bulkDeleteVariboxes(ids: string[]) {
  const response = await http.post<BulkDeleteVariboxesResponse>('/api/variboxes/actions/bulk-delete', {
    ids,
  });
  return response.data;
}
